import { z } from "zod";
import type { EscoConceptFieldsFragment } from "@/graphql/generated/operations";
export const escoReferenceSchema = z.object({
  uri: z.string(), label: z.string(), version: z.string(), language: z.string(), description: z.string().nullish(),
  source: z.enum(["USER_SELECTED", "AUTOMATIC"]), status: z.enum(["CONFIRMED", "PROPOSED"]), basedOnText: z.string(),
});
const classificationV1Schema = z.object({
  version: z.literal(1),
  entryOrigin: z.enum(["UNKNOWN", "MANUAL", "DOCUMENT_IMPORT", "AI_SUGGESTION", "ESCO_SELECTION"]),
  references: z.array(escoReferenceSchema).max(5),
});
export const classificationSchema = z.preprocess(value => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return value;
  const legacy = value as Record<string, unknown>;
  return !("version" in legacy) && "schemaVersion" in legacy
    ? { ...legacy, version: legacy.schemaVersion } : value;
}, classificationV1Schema);
export type Classification = z.infer<typeof classificationSchema>;
export type EscoReference = z.infer<typeof escoReferenceSchema>;
export type EscoConcept = EscoConceptFieldsFragment;
export function readClassification(value: unknown): Classification | null {
  try {
    const parsed = classificationSchema.safeParse(typeof value === "string" ? JSON.parse(value) : value);
    return parsed.success ? parsed.data : null;
  } catch { return null; }
}
export function selectedClassification(concept: EscoConcept, text: string, previous?: unknown, multiple = false): Classification {
  const old = readClassification(previous);
  const reference: EscoReference = { uri: concept.uri, label: concept.label, version: concept.version,
    language: concept.language, description: concept.description, source: "USER_SELECTED", status: "CONFIRMED", basedOnText: text };
  return { version: 1, entryOrigin: old?.entryOrigin ?? "UNKNOWN",
    references: multiple ? [...(old?.references ?? []).filter(r => r.uri !== concept.uri), reference].slice(0, 5) : [reference] };
}
export function classificationForText(value: unknown, text: string): Classification | null {
  const c = readClassification(value);
  return c ? { ...c, references: c.references.map(r => r.basedOnText === text ? r : { ...r, status: "PROPOSED" }) } : null;
}

export type EscoLanguage = "de" | "en";
